import Vue from "vue";
import _ from "lodash";
import {
    FetchState,
    ReimburseMeState,
    ReimbursementRequest,
    ReimburseBankRequest,
    UpdateReimbursementRequest,
    ReimbursementGetResponse,
    UnreimbursedPage
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import * as expenseService from "@/shared/api/expenseService";
import { formatCurrency } from "@/shared/currency";
import { getContactInformation } from "@/shared/api/userService";
import { defineStore } from "pinia";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import {
    useHsaPendingTransactionStore
} from "@/shared/store/hsaPendingTransaction";
import { useExpenseBalanceSummaryStore } from "@/employee/store/expenseBalanceSummary";
import { useExpenseUnreimbursedStore } from "@/employee/store/expenseUnreimbursed";
import { useExpenseReimbursedStore } from "@/employee/store/expenseReimbursed";
import { useNotifierStore } from "@/shared/store/notifier";

// Values here are fake data until backend calls are available
const initialState: ReimburseMeState = {
    cashTotal: 0,
    unreimbursedTotal: 0,
    fetchState: FetchState.Uninitialized,
    page: 1,
    pageSize: 10,
    numberOfPages: 1,
    numberOfReimbursements: 0,
    records: [],
};

export const useReimburseMeStore = defineStore("reimburseMe", {
    state: (): ReimburseMeState => (_.cloneDeep(initialState)),
    getters: {
        getCashTotal: state => state.cashTotal,
        getUnreimbursedTotal: state => state.unreimbursedTotal,
        getList: state => page => {
            if (page <= 0 || page > state.numberOfPages) {
                return [];
            }
            return state.records.slice((page - 1) * state.pageSize, page * state.pageSize);
        },
        hasPages: state => state.numberOfPages > 1,
        currentPage: state => state.page,
        totalNumberOfReimbursements: state => state.numberOfReimbursements,
        pageCount: state => state.numberOfPages,
        pageRange: state => _.range(state.page - 2, state.page + 3)
            .filter(page => page > 0 && page <= state.numberOfPages),
        getFetchState: state => state.fetchState
    },
    actions: {
        setCashTotal (cashTotal: number): void {
            this.cashTotal = cashTotal;
        },
        setUnreimbursedTotal (unreimbursedTotal: number): void {
            this.unreimbursedTotal = unreimbursedTotal;
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setFromResponse (response: ReimbursementGetResponse) {
            this.page = response.page;
            this.numberOfPages = response.numberOfPages;
            this.numberOfReimbursements = response.numberOfReimbursements;

            let sIndex = (this.page - 1) * this.pageSize;
            const rlen = response.records.length;
            for (let index = 0; index < rlen; index++, sIndex++) {
                Vue.set(this.records, sIndex, response.records[index]);
            }

            const slen = this.records.length;
            for (; sIndex < slen; sIndex++) {
                Vue.delete(this.records, sIndex);
            }

            this.fetchState = FetchState.Loaded;
        },
        load () {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);
            return expenseService.reimbursedGet(this.page, this.pageSize).then(response => {
                this.setFromResponse(response.data);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPage (request: UnreimbursedPage) {
            if (this.fetchState !== FetchState.Loaded || !request || request.page <= 0 || request.page >
                this.numberOfPages) {
                return Promise.resolve();
            }

            const elist = this.getList(request.page);
            const fullPage = _.every(elist, _.identity);
            if (elist.length && fullPage && !request.force) {
                this.page = request.page;
                return Promise.resolve();
            }

            return expenseService.reimbursedGet(request.page, this.pageSize).then(response => {
                this.setFromResponse(response.data);
            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reimburse (request: ReimbursementRequest): Promise<any> {
            const bankRequest: ReimburseBankRequest = {
                amount: request.amount,
                reimbursementLinkedAccountId: request.reimbursementLinkedAccountId
            };
            const updateReimburseRequest: UpdateReimbursementRequest = {
                reimbursementRequestId: null,
                transactionId: null,
                custodianTransactionId: null,
                isBankRequestSuccess: true,
                description: null,
            };

            return expenseService.reimbursementRequest(request)
                .then(expResp => {
                    updateReimburseRequest.reimbursementRequestId = expResp.data.reimbursementRequestId;
                    return bankService.reimburseRequest(bankRequest);
                })
                .then(result => {
                    updateReimburseRequest.description = result.data.description;
                    updateReimburseRequest.transactionId = result.data.transactionId;
                    updateReimburseRequest.custodianTransactionId = result.data.custodianTransactionId;
                    return expenseService.updateReimbursementRequest(updateReimburseRequest);
                })
                .then(result => {
                    const hsaDailyBalanceStore = useHsaDailyBalanceStore();
                    const hsaPendingTransactionStore = useHsaPendingTransactionStore();
                    const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
                    const expenseReinbursedStore = useExpenseReimbursedStore();
                    const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
                    const notifierStore = useNotifierStore();

                    hsaDailyBalanceStore.loadInstantBalance({ force: true });
                    hsaPendingTransactionStore.clearPendingTransactions();
                    expenseBalanceSummaryStore.retrieveBalanceSummary();
                    expenseReinbursedStore.reloadPage();
                    if (request.expenseIds) {
                        expenseUnreimbursedStore.reloadPage();
                    } else {
                        expenseUnreimbursedStore.resetUnreimbursedState();
                    }
                    notifierStore.setSuccess(`We received your request and are processing it.`);
                })
                .catch(err => {
                    if (updateReimburseRequest.reimbursementRequestId) {
                        updateReimburseRequest.isBankRequestSuccess = false;
                        expenseService.updateReimbursementRequest(updateReimburseRequest)
                            .catch(err);
                    }
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        manualMarkedAsReimbursed (
            request: ReimbursementRequest): Promise<any> {
            const hsaDailyBalanceStore = useHsaDailyBalanceStore();
            const hsaPendingTransactionStore = useHsaPendingTransactionStore();
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReinbursedStore = useExpenseReimbursedStore();
            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            const notifierStore = useNotifierStore();
            return expenseService.manualMarkedAsReimbursed(request)
                .then(x => {
                    hsaDailyBalanceStore.loadInstantBalance({ force: true });
                    hsaPendingTransactionStore.clearPendingTransactions();
                    expenseBalanceSummaryStore.retrieveBalanceSummary();
                    expenseUnreimbursedStore.reloadPage();
                    expenseReinbursedStore.reloadPage();
                    expenseBalanceSummaryStore.reimburseMeDismiss();
                    notifierStore.setSuccess(`Your expense has been marked as reimbursed.`);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        reloadPage () {
            this.loadPage({
                page: this.page,
                force: true
            });
        },
    }
});
